import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Activity, ChevronRight, Plus, Loader2, TrendingUp, Clock, 
  Zap, Heart, BarChart3, Calendar, Upload, X, Download,
  Info, Target
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  AreaChart,
  Area,
  BarChart,
  Bar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import SafeResponsiveContainer from './ui/SafeResponsiveContainer';

import { API } from '../config/api';

// Wattbike metrics with their descriptions
const WATTBIKE_METRICS = {
  max_minute_power: { label: 'Max Minute Power (MMP)', unit: 'W', description: 'Highest average power over 1 minute' },
  ftp: { label: 'FTP', unit: 'W', description: 'Functional Threshold Power - max sustainable power for ~1hr' },
  peak_power: { label: 'Peak Power', unit: 'W', description: 'Highest instantaneous power output' },
  avg_power: { label: 'Average Power', unit: 'W', description: 'Mean power over the session' },
  cadence_avg: { label: 'Avg Cadence', unit: 'rpm', description: 'Average pedal revolutions per minute' },
  cadence_max: { label: 'Max Cadence', unit: 'rpm', description: 'Peak cadence achieved' },
  avg_heart_rate: { label: 'Avg Heart Rate', unit: 'bpm', description: 'Average heart rate during session' },
  max_heart_rate: { label: 'Max Heart Rate', unit: 'bpm', description: 'Peak heart rate achieved' },
  duration_seconds: { label: 'Duration', unit: 'sec', description: 'Total session time' },
  distance_meters: { label: 'Distance', unit: 'm', description: 'Total distance covered' },
  calories: { label: 'Calories', unit: 'kcal', description: 'Estimated energy expenditure' },
  pedal_effectiveness_left: { label: 'PES Left', unit: '%', description: 'Left leg pedaling effectiveness' },
  pedal_effectiveness_right: { label: 'PES Right', unit: '%', description: 'Right leg pedaling effectiveness' },
};

// Power zones based on FTP
const getPowerZones = (ftp) => {
  if (!ftp) return [];
  return [
    { zone: 1, name: 'Active Recovery', min: 0, max: Math.round(ftp * 0.55), color: '#94a3b8' },
    { zone: 2, name: 'Endurance', min: Math.round(ftp * 0.56), max: Math.round(ftp * 0.75), color: '#22c55e' },
    { zone: 3, name: 'Tempo', min: Math.round(ftp * 0.76), max: Math.round(ftp * 0.90), color: '#eab308' },
    { zone: 4, name: 'Threshold', min: Math.round(ftp * 0.91), max: Math.round(ftp * 1.05), color: '#f97316' },
    { zone: 5, name: 'VO2 Max', min: Math.round(ftp * 1.06), max: Math.round(ftp * 1.20), color: '#ef4444' },
    { zone: 6, name: 'Anaerobic', min: Math.round(ftp * 1.21), max: Math.round(ftp * 1.50), color: '#a855f7' },
    { zone: 7, name: 'Neuromuscular', min: Math.round(ftp * 1.51), max: ftp * 3, color: '#ec4899' },
  ];
};

export default function WattbikeData({ childId, childName }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'charts', 'zones'
  const [latestFTP, setLatestFTP] = useState(null);
  
  const [formData, setFormData] = useState({
    session_date: new Date().toISOString().split('T')[0],
    session_type: 'training',
    max_minute_power: '',
    ftp: '',
    peak_power: '',
    avg_power: '',
    cadence_avg: '',
    cadence_max: '',
    avg_heart_rate: '',
    max_heart_rate: '',
    duration_seconds: '',
    distance_meters: '',
    calories: '',
    pedal_effectiveness_left: '',
    pedal_effectiveness_right: '',
    notes: ''
  });

  const loadSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/wattbike/sessions/${childId}`);
      setSessions(res.data.sessions || []);
      if (res.data.latest_ftp) {
        setLatestFTP(res.data.latest_ftp);
      }
    } catch (err) {
      console.error('Failed to load Wattbike sessions:', err);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleSaveSession = async () => {
    if (!formData.session_date) {
      toast.error('Please select a session date');
      return;
    }

    setSaving(true);
    try {
      // Convert string values to numbers
      const sessionData = {
        child_id: childId,
        session_date: formData.session_date,
        session_type: formData.session_type,
        notes: formData.notes
      };
      
      // Add numeric fields only if they have values
      Object.keys(WATTBIKE_METRICS).forEach(key => {
        if (formData[key] && formData[key] !== '') {
          sessionData[key] = parseFloat(formData[key]);
        }
      });

      await axios.post(`${API}/wattbike/sessions`, sessionData);
      toast.success('Wattbike session saved!');
      setShowAddForm(false);
      setFormData({
        session_date: new Date().toISOString().split('T')[0],
        session_type: 'training',
        max_minute_power: '',
        ftp: '',
        peak_power: '',
        avg_power: '',
        cadence_avg: '',
        cadence_max: '',
        avg_heart_rate: '',
        max_heart_rate: '',
        duration_seconds: '',
        distance_meters: '',
        calories: '',
        pedal_effectiveness_left: '',
        pedal_effectiveness_right: '',
        notes: ''
      });
      loadSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save session');
    } finally {
      setSaving(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Prepare chart data
  const chartData = sessions.slice().reverse().map(s => ({
    date: new Date(s.session_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
    ftp: s.ftp,
    mmp: s.max_minute_power,
    avgPower: s.avg_power,
    peakPower: s.peak_power,
    avgHR: s.avg_heart_rate,
    maxHR: s.max_heart_rate,
    cadence: s.cadence_avg
  }));

  // Radar chart data for latest session vs best values
  const getRadarData = () => {
    if (sessions.length === 0) return [];
    
    const latest = sessions[0];
    const best = {
      ftp: Math.max(...sessions.map(s => s.ftp || 0)),
      peak_power: Math.max(...sessions.map(s => s.peak_power || 0)),
      max_minute_power: Math.max(...sessions.map(s => s.max_minute_power || 0)),
      avg_power: Math.max(...sessions.map(s => s.avg_power || 0)),
      cadence_avg: Math.max(...sessions.map(s => s.cadence_avg || 0)),
    };

    return [
      { metric: 'FTP', latest: latest.ftp || 0, best: best.ftp, fullMark: best.ftp * 1.1 },
      { metric: 'Peak Power', latest: latest.peak_power || 0, best: best.peak_power, fullMark: best.peak_power * 1.1 },
      { metric: 'MMP', latest: latest.max_minute_power || 0, best: best.max_minute_power, fullMark: best.max_minute_power * 1.1 },
      { metric: 'Avg Power', latest: latest.avg_power || 0, best: best.avg_power, fullMark: best.avg_power * 1.1 },
      { metric: 'Cadence', latest: latest.cadence_avg || 0, best: best.cadence_avg, fullMark: best.cadence_avg * 1.1 },
    ];
  };

  const powerZones = getPowerZones(latestFTP);

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center">
        <Loader2 className="h-8 w-8 text-[#02b5ff] animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="wattbike-data">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 border-b border-orange-500/30 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <Activity className="h-5 w-5 text-orange-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                Wattbike Data
                <span className="text-xs bg-orange-500/30 text-orange-300 px-2 py-0.5 rounded-full">
                  {sessions.length} sessions
                </span>
              </h3>
              <p className="text-xs text-zinc-400">Indoor cycling performance metrics</p>
            </div>
          </div>
          <div className="flex gap-2">
            <div className="flex bg-zinc-800 rounded-lg p-0.5">
              {['list', 'charts', 'zones'].map(mode => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    viewMode === mode ? 'bg-orange-500 text-white' : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-orange-500 hover:bg-orange-400 text-white text-xs h-8"
              data-testid="add-wattbike-session"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Session
            </Button>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-zinc-800/50 border-b border-zinc-700 px-4 py-2 flex items-center gap-2">
        <Info className="h-4 w-4 text-zinc-500" />
        <p className="text-xs text-zinc-400">
          Wattbike Hub doesn't have a public API. Enter your data manually or export .FIT files from the Hub app.
        </p>
      </div>

      {/* Add Session Form */}
      {showAddForm && (
        <div className="p-4 bg-zinc-800/30 border-b border-zinc-700">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-white">Add Wattbike Session</h4>
            <button onClick={() => setShowAddForm(false)} className="text-zinc-500 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="col-span-2 md:col-span-1">
              <label className="text-xs text-zinc-500 block mb-1">Session Date</label>
              <Input
                type="date"
                value={formData.session_date}
                onChange={(e) => setFormData({ ...formData, session_date: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 block mb-1">Type</label>
              <select
                value={formData.session_type}
                onChange={(e) => setFormData({ ...formData, session_type: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white text-sm"
              >
                <option value="training">Training</option>
                <option value="test">FTP Test</option>
                <option value="race">Race</option>
                <option value="recovery">Recovery</option>
              </select>
            </div>
          </div>

          {/* Power Metrics */}
          <div className="mb-4">
            <h5 className="text-xs text-orange-400 font-medium mb-2 flex items-center gap-1">
              <Zap className="h-3 w-3" /> Power Metrics
            </h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {['ftp', 'max_minute_power', 'peak_power', 'avg_power'].map(key => (
                <div key={key}>
                  <label className="text-[10px] text-zinc-500 block mb-1">
                    {WATTBIKE_METRICS[key].label}
                  </label>
                  <Input
                    type="number"
                    value={formData[key]}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    placeholder={WATTBIKE_METRICS[key].unit}
                    className="bg-zinc-800 border-zinc-700 text-white text-sm"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Cadence & HR */}
          <div className="mb-4">
            <h5 className="text-xs text-red-400 font-medium mb-2 flex items-center gap-1">
              <Heart className="h-3 w-3" /> Cadence & Heart Rate
            </h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {['cadence_avg', 'cadence_max', 'avg_heart_rate', 'max_heart_rate'].map(key => (
                <div key={key}>
                  <label className="text-[10px] text-zinc-500 block mb-1">
                    {WATTBIKE_METRICS[key].label}
                  </label>
                  <Input
                    type="number"
                    value={formData[key]}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    placeholder={WATTBIKE_METRICS[key].unit}
                    className="bg-zinc-800 border-zinc-700 text-white text-sm"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Session Stats */}
          <div className="mb-4">
            <h5 className="text-xs text-blue-400 font-medium mb-2 flex items-center gap-1">
              <Clock className="h-3 w-3" /> Session Stats
            </h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {['duration_seconds', 'distance_meters', 'calories'].map(key => (
                <div key={key}>
                  <label className="text-[10px] text-zinc-500 block mb-1">
                    {WATTBIKE_METRICS[key].label}
                  </label>
                  <Input
                    type="number"
                    value={formData[key]}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    placeholder={WATTBIKE_METRICS[key].unit}
                    className="bg-zinc-800 border-zinc-700 text-white text-sm"
                  />
                </div>
              ))}
              <div>
                <label className="text-[10px] text-zinc-500 block mb-1">PES Left/Right (%)</label>
                <div className="flex gap-1">
                  <Input
                    type="number"
                    value={formData.pedal_effectiveness_left}
                    onChange={(e) => setFormData({ ...formData, pedal_effectiveness_left: e.target.value })}
                    placeholder="L"
                    className="bg-zinc-800 border-zinc-700 text-white text-sm w-1/2"
                  />
                  <Input
                    type="number"
                    value={formData.pedal_effectiveness_right}
                    onChange={(e) => setFormData({ ...formData, pedal_effectiveness_right: e.target.value })}
                    placeholder="R"
                    className="bg-zinc-800 border-zinc-700 text-white text-sm w-1/2"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-4">
            <label className="text-xs text-zinc-500 block mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white text-sm resize-none"
              rows={2}
              placeholder="Workout details, how you felt, etc."
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAddForm(false)}
              className="border-zinc-700 text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSession}
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-400 text-white"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Save Session
            </Button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {sessions.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="h-12 w-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm mb-4">No Wattbike sessions recorded yet</p>
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-orange-500/20 text-orange-400 border border-orange-500/30 hover:bg-orange-500/30"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add First Session
            </Button>
          </div>
        ) : viewMode === 'list' ? (
          /* Sessions List */
          <div className="space-y-3">
            {sessions.map(session => (
              <div
                key={session.id}
                className="bg-zinc-800/50 rounded-lg p-3 hover:bg-zinc-800 transition-colors cursor-pointer"
                onClick={() => setSelectedSession(selectedSession?.id === session.id ? null : session)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      session.session_type === 'test' ? 'bg-purple-500/20' :
                      session.session_type === 'race' ? 'bg-red-500/20' :
                      session.session_type === 'recovery' ? 'bg-green-500/20' :
                      'bg-orange-500/20'
                    }`}>
                      <Activity className={`h-4 w-4 ${
                        session.session_type === 'test' ? 'text-purple-400' :
                        session.session_type === 'race' ? 'text-red-400' :
                        session.session_type === 'recovery' ? 'text-green-400' :
                        'text-orange-400'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white">
                          {new Date(session.session_date).toLocaleDateString('en-GB', { 
                            weekday: 'short', day: 'numeric', month: 'short' 
                          })}
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                          session.session_type === 'test' ? 'bg-purple-500/30 text-purple-300' :
                          session.session_type === 'race' ? 'bg-red-500/30 text-red-300' :
                          session.session_type === 'recovery' ? 'bg-green-500/30 text-green-300' :
                          'bg-orange-500/30 text-orange-300'
                        }`}>
                          {session.session_type}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1">
                        {session.avg_power && (
                          <span className="text-xs text-zinc-400">
                            <Zap className="h-3 w-3 inline mr-1 text-orange-400" />
                            {session.avg_power}W avg
                          </span>
                        )}
                        {session.duration_seconds && (
                          <span className="text-xs text-zinc-400">
                            <Clock className="h-3 w-3 inline mr-1" />
                            {formatDuration(session.duration_seconds)}
                          </span>
                        )}
                        {session.avg_heart_rate && (
                          <span className="text-xs text-zinc-400">
                            <Heart className="h-3 w-3 inline mr-1 text-red-400" />
                            {session.avg_heart_rate} bpm
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    {session.ftp && (
                      <div className="text-lg font-bold text-orange-400">{session.ftp}W</div>
                    )}
                    {session.ftp && <div className="text-[10px] text-zinc-500">FTP</div>}
                  </div>
                </div>

                {/* Expanded details */}
                {selectedSession?.id === session.id && (
                  <div className="mt-3 pt-3 border-t border-zinc-700 grid grid-cols-3 md:grid-cols-5 gap-3">
                    {session.peak_power && (
                      <div>
                        <div className="text-xs text-zinc-500">Peak Power</div>
                        <div className="text-sm font-medium text-white">{session.peak_power}W</div>
                      </div>
                    )}
                    {session.max_minute_power && (
                      <div>
                        <div className="text-xs text-zinc-500">MMP</div>
                        <div className="text-sm font-medium text-white">{session.max_minute_power}W</div>
                      </div>
                    )}
                    {session.cadence_avg && (
                      <div>
                        <div className="text-xs text-zinc-500">Avg Cadence</div>
                        <div className="text-sm font-medium text-white">{session.cadence_avg} rpm</div>
                      </div>
                    )}
                    {session.distance_meters && (
                      <div>
                        <div className="text-xs text-zinc-500">Distance</div>
                        <div className="text-sm font-medium text-white">{(session.distance_meters / 1000).toFixed(2)} km</div>
                      </div>
                    )}
                    {session.calories && (
                      <div>
                        <div className="text-xs text-zinc-500">Calories</div>
                        <div className="text-sm font-medium text-white">{session.calories} kcal</div>
                      </div>
                    )}
                    {session.notes && (
                      <div className="col-span-full">
                        <div className="text-xs text-zinc-500">Notes</div>
                        <div className="text-sm text-zinc-300">{session.notes}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : viewMode === 'charts' ? (
          /* Charts View */
          <div className="space-y-6">
            {/* FTP Trend */}
            <div className="bg-zinc-800/30 rounded-lg p-4">
              <h4 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-orange-400" />
                Power Progression
              </h4>
              <div className="h-64">
                <SafeResponsiveContainer width="100%" height="100%" minHeight={200}>
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="ftpGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="date" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                    <YAxis stroke="#9ca3af" tick={{ fontSize: 10 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Area type="monotone" dataKey="ftp" name="FTP" stroke="#f97316" fill="url(#ftpGradient)" />
                    <Line type="monotone" dataKey="avgPower" name="Avg Power" stroke="#22c55e" dot={false} />
                    <Line type="monotone" dataKey="peakPower" name="Peak Power" stroke="#ef4444" dot={false} />
                  </AreaChart>
                </SafeResponsiveContainer>
              </div>
            </div>

            {/* Radar Comparison */}
            {sessions.length > 0 && (
              <div className="bg-zinc-800/30 rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-purple-400" />
                  Latest vs Personal Bests
                </h4>
                <div className="h-64">
                  <SafeResponsiveContainer width="100%" height="100%" minHeight={200}>
                    <RadarChart data={getRadarData()}>
                      <PolarGrid stroke="#374151" />
                      <PolarAngleAxis dataKey="metric" tick={{ fill: '#9ca3af', fontSize: 10 }} />
                      <PolarRadiusAxis tick={{ fill: '#6b7280', fontSize: 9 }} />
                      <Radar name="Latest" dataKey="latest" stroke="#02b5ff" fill="#02b5ff" fillOpacity={0.3} />
                      <Radar name="Best" dataKey="best" stroke="#f97316" fill="#f97316" fillOpacity={0.2} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px' }}
                      />
                    </RadarChart>
                  </SafeResponsiveContainer>
                </div>
              </div>
            )}

            {/* Heart Rate Trend */}
            <div className="bg-zinc-800/30 rounded-lg p-4">
              <h4 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                <Heart className="h-4 w-4 text-red-400" />
                Heart Rate & Cadence
              </h4>
              <div className="h-48">
                <SafeResponsiveContainer width="100%" height="100%" minHeight={200}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="date" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="hr" stroke="#ef4444" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="cadence" orientation="right" stroke="#22c55e" tick={{ fontSize: 10 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px' }}
                    />
                    <Line yAxisId="hr" type="monotone" dataKey="avgHR" name="Avg HR" stroke="#ef4444" dot={{ r: 3 }} />
                    <Line yAxisId="hr" type="monotone" dataKey="maxHR" name="Max HR" stroke="#f97316" dot={{ r: 3 }} />
                    <Line yAxisId="cadence" type="monotone" dataKey="cadence" name="Cadence" stroke="#22c55e" dot={{ r: 3 }} />
                  </LineChart>
                </SafeResponsiveContainer>
              </div>
            </div>
          </div>
        ) : (
          /* Power Zones View */
          <div className="space-y-4">
            {latestFTP ? (
              <>
                <div className="bg-zinc-800/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-medium text-white flex items-center gap-2">
                      <Target className="h-4 w-4 text-orange-400" />
                      Power Zones (Based on FTP: {latestFTP}W)
                    </h4>
                  </div>
                  <div className="space-y-2">
                    {powerZones.map(zone => (
                      <div key={zone.zone} className="flex items-center gap-3">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold"
                          style={{ backgroundColor: zone.color }}
                        >
                          {zone.zone}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-white">{zone.name}</span>
                            <span className="text-xs text-zinc-400">{zone.min} - {zone.max}W</span>
                          </div>
                          <div className="h-2 bg-zinc-700 rounded-full mt-1 overflow-hidden">
                            <div 
                              className="h-full rounded-full"
                              style={{ 
                                backgroundColor: zone.color,
                                width: `${((zone.max - zone.min) / latestFTP) * 50}%`
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Zone Distribution from Sessions */}
                <div className="bg-zinc-800/30 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-white mb-3">Zone Training Tips</h4>
                  <div className="space-y-2 text-xs text-zinc-400">
                    <p><span className="text-green-400 font-medium">Zone 2 (Endurance)</span>: Build aerobic base. 60-75% of training time.</p>
                    <p><span className="text-yellow-400 font-medium">Zone 3 (Tempo)</span>: Sweet spot training. 15-20% of training.</p>
                    <p><span className="text-orange-400 font-medium">Zone 4 (Threshold)</span>: FTP improvement. 10-15% of training.</p>
                    <p><span className="text-red-400 font-medium">Zone 5-7</span>: High intensity intervals. 5-10% of training.</p>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12 bg-zinc-800/30 rounded-lg">
                <Target className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400 text-sm mb-2">No FTP data recorded yet</p>
                <p className="text-xs text-zinc-500">Complete an FTP test to see your power zones</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
